import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Radio } from "lucide-react";

import { StateBadge, type StateBadgeKind } from "../../components/StateBadge";
import { PageHeader } from "../../components/ui/PageHeader";
import { Timestamp } from "../../components/ui/Timestamp";
import { useLiveCalls } from "../../hooks/useCalls";
import { useEngagements } from "../../hooks/useEngagements";
import type { CallListItem } from "../../api/calls";
import { LiveCard } from "./LiveCard";
import { ScrollableTable } from "../../components/ScrollableTable";
import { LanguageBadge, type Language } from "../../components/LanguageBadge";
import {
  loadHiddenColumns,
  saveHiddenColumns,
  toggleHidden,
  visibleColumns,
} from "./livecalls-columns";

const VERDICT_KIND: Record<string, StateBadgeKind> = {
  CLEAN: "clean",
  REJECT: "warn",
  FAILED: "error",
};

const STATE_KIND: Record<string, StateBadgeKind> = {
  LIVE: "live",
  ENDED: "info",
  HALTED: "error",
};

type StateFilter =
  | "ALL"
  | "LIVE"
  | "ENDED"
  | "HALTED"
  | "VOICEMAIL"
  | "WRONG_NUMBER"
  | "AGENT_ENDED";

// Maps the filter key → predicate over the call row. Reason-based
// filters intersect with state != LIVE so they only show completed
// calls (an in-flight call can't yet have a termination_reason).
function matchesFilter(call: CallListItem, filter: StateFilter): boolean {
  if (filter === "ALL") return true;
  if (filter === "ENDED") return call.state === "ENDED";
  if (filter === "HALTED") return call.state === "HALTED";
  if (filter === "LIVE") return call.state === "LIVE";
  const reason = call.termination_reason ?? "";
  if (filter === "VOICEMAIL") return reason.includes("voicemail_detected");
  if (filter === "WRONG_NUMBER") return reason.includes("wrong_number");
  if (filter === "AGENT_ENDED") return reason.startsWith("agent_initiated:");
  return true;
}

// ---------------------------------------------------------------------------
// Column descriptor types
// ---------------------------------------------------------------------------

interface ColumnCtx {
  engagementLabel: string | undefined;
}

interface CallColumn {
  key: string;
  label: string;
  width: string;
  align?: "right";
  pinned?: boolean;
  render: (call: CallListItem, ctx: ColumnCtx) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Per-column render functions (cell content only; RecentRow wraps each in
// a plain <span style={{minWidth:0}}> for grid layout).
// ---------------------------------------------------------------------------

function renderCallCell(call: CallListItem) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "var(--text-primary)",
        letterSpacing: "0.04em",
      }}
    >
      {call.has_recording ? (
        <span
          data-testid="recording-indicator"
          title="Recording attempted"
          style={{
            width: 16,
            height: 16,
            display: "grid",
            placeItems: "center",
            border: "1px solid var(--accent-line)",
            color: "var(--accent)",
            fontSize: 9,
          }}
        >
          ▶
        </span>
      ) : (
        <span style={{ width: 16 }} />
      )}
      <span>{call.call_id.slice(0, 8)}</span>
      {call.sip_trunk_label ? (
        <span
          title={`SIP carrier: ${call.sip_trunk_label}`}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--text-muted)",
            padding: "1px 5px",
            border: "1px solid var(--border-subtle)",
            borderRadius: 3,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {call.sip_trunk_label}
        </span>
      ) : null}
    </span>
  );
}

function renderEngagementCell(call: CallListItem, ctx: ColumnCtx) {
  const engagementLabel = ctx.engagementLabel;
  return (
    <span
      style={{
        color: engagementLabel ? "var(--text-secondary)" : "var(--text-muted)",
        fontFamily: engagementLabel ? "var(--font-sans)" : "var(--font-mono)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {engagementLabel ?? `${call.engagement_id.slice(0, 8)}…`}
    </span>
  );
}

function renderTargetCell(call: CallListItem) {
  return (
    <span
      title={call.target_phone ?? undefined}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: call.target_label ? "var(--text-secondary)" : "var(--text-muted)",
      }}
    >
      {call.target_label ?? "—"}
    </span>
  );
}

function renderLanguageCell(call: CallListItem) {
  return call.language ? (
    <LanguageBadge language={call.language as Language} />
  ) : (
    <span style={{ color: "var(--text-muted)" }}>—</span>
  );
}

function renderStateCell(call: CallListItem) {
  return <StateBadge kind={STATE_KIND[call.state] ?? "info"}>{call.state}</StateBadge>;
}

function renderVerdictCell(call: CallListItem) {
  const kind: StateBadgeKind = call.verdict ? VERDICT_KIND[call.verdict] ?? "info" : "info";
  return <StateBadge kind={kind}>{call.verdict ?? "—"}</StateBadge>;
}

function renderTurnsCell(call: CallListItem) {
  return (
    <span
      style={{
        color: "var(--text-secondary)",
        fontFamily: "var(--font-mono)",
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {call.turn_count}
    </span>
  );
}

function renderReasonCell(call: CallListItem) {
  return <ReasonCell reason={call.termination_reason} />;
}

function renderWhenCell(call: CallListItem) {
  return <Timestamp iso={call.ended_at ?? call.started_at} variant="stacked" align="right" />;
}

function renderChevronCell() {
  return <ChevronRight size={13} strokeWidth={1.5} color="var(--text-muted)" />;
}

// ---------------------------------------------------------------------------
// Column registry
// ---------------------------------------------------------------------------

const CALL_COLUMNS: CallColumn[] = [
  { key: "call", label: "Call", width: "minmax(180px, 1.4fr)", pinned: true, render: renderCallCell },
  { key: "engagement", label: "Engagement", width: "minmax(160px, 1.2fr)", render: renderEngagementCell },
  { key: "target", label: "Target", width: "minmax(140px, 1fr)", render: renderTargetCell },
  { key: "language", label: "Language", width: "80px", render: renderLanguageCell },
  { key: "state", label: "State", width: "100px", render: renderStateCell },
  { key: "verdict", label: "Verdict", width: "100px", render: renderVerdictCell },
  { key: "turns", label: "Turns", width: "56px", align: "right", render: renderTurnsCell },
  { key: "reason", label: "Reason", width: "130px", render: renderReasonCell },
  { key: "when", label: "When", width: "130px", align: "right", render: renderWhenCell },
  { key: "chevron", label: "", width: "18px", pinned: true, render: renderChevronCell },
];

// ---------------------------------------------------------------------------
// Grid template helper
// ---------------------------------------------------------------------------

function gridTemplate(cols: CallColumn[]): string {
  return cols.map((c) => c.width).join(" ");
}

// Sum of all fixed tracks + minmax floors + gaps + row padding.
// With all columns visible this is wider than the original 880 to
// accommodate the two new columns (Target + Language).
const RECENT_TABLE_MIN_WIDTH = 1050;

// ---------------------------------------------------------------------------
// ColumnHeader + RecentRow (data-driven)
// ---------------------------------------------------------------------------

function ColumnHeader({ cols }: { cols: CallColumn[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: gridTemplate(cols),
        padding: "11px 18px",
        borderBottom: "1px solid var(--border-subtle)",
        background: "var(--bg-secondary)",
        gap: 12,
        fontSize: 9.5,
        fontFamily: "var(--font-mono)",
        textTransform: "uppercase",
        letterSpacing: "0.16em",
        color: "var(--text-muted)",
      }}
    >
      {cols.map((c) => (
        <span key={c.key} style={c.align === "right" ? { textAlign: "right" } : undefined}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

function RecentRow({
  call,
  cols,
  ctx,
  index,
}: {
  call: CallListItem;
  cols: CallColumn[];
  ctx: ColumnCtx;
  index: number;
}) {
  return (
    <Link
      to={`/live-calls/${call.call_id}`}
      className="animate-in"
      style={{
        display: "grid",
        gridTemplateColumns: gridTemplate(cols),
        padding: "13px 18px",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
        gap: 12,
        fontSize: 11.5,
        color: "var(--text-secondary)",
        textDecoration: "none",
        animationDelay: `${index * 30}ms`,
      }}
      title={`call_id ${call.call_id}\nengagement_id ${call.engagement_id}`}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background = "var(--bg-hover)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
      }}
    >
      {cols.map((c) => (
        <span key={c.key} style={{ minWidth: 0 }}>
          {c.render(call, ctx)}
        </span>
      ))}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// ColumnsPicker
// ---------------------------------------------------------------------------

function ColumnsPicker({
  columns,
  hidden,
  onToggle,
}: {
  columns: CallColumn[];
  hidden: string[];
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const toggleable = columns.filter((c) => !c.pinned);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "7px 14px",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-input)",
          background: open ? "var(--accent-subtle)" : "var(--bg-secondary)",
          color: open ? "var(--accent)" : "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          cursor: "pointer",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          fontWeight: 500,
        }}
      >
        Columns ▾
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 20,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: 8,
            minWidth: 180,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          }}
        >
          {toggleable.map((c) => (
            <label
              key={c.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                fontSize: 12,
                color: "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!hidden.includes(c.key)}
                onChange={() => onToggle(c.key)}
              />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function LiveCallsList() {
  const { data, isLoading, error } = useLiveCalls();
  const { data: engagementsData } = useEngagements();
  const [stateFilter, setStateFilter] = useState<StateFilter>("ALL");
  const [hidden, setHidden] = useState<string[]>(() => loadHiddenColumns());

  const cols = visibleColumns(CALL_COLUMNS, hidden);

  function onToggleColumn(key: string) {
    setHidden((prev) => {
      const next = toggleHidden(prev, key);
      saveHiddenColumns(next);
      return next;
    });
  }

  const engagementName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of engagementsData?.items ?? []) {
      m.set(e.engagement_id, e.display_name);
    }
    return m;
  }, [engagementsData]);

  if (isLoading)
    return <StatusBlock label="Acquiring call feed…" />;
  if (error)
    return <StatusBlock label={String(error)} tone="error" />;
  if (!data) return null;

  const live = data.items.filter((c) => c.state === "LIVE");
  const recentAll = data.items.filter((c) => c.state !== "LIVE");
  const recent =
    stateFilter === "ALL" || stateFilter === "LIVE"
      ? recentAll
      : recentAll.filter((c) => matchesFilter(c, stateFilter));

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow="Operator console"
        title="Active operations"
        subtitle="Real-time feed of in-flight calls. Click any row to open the live transcript, recording, and audit chain."
        meta={
          <span>
            <span style={{ color: "var(--accent)" }}>{live.length}</span> live
            <Dot />
            <span style={{ color: "var(--text-secondary)" }}>{recentAll.length}</span> recent
          </span>
        }
      />

      {/* LIVE section */}
      <SectionHeader
        eyebrow="In flight"
        title={`In flight (${live.length})`}
        accent
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
          marginBottom: 32,
        }}
      >
        {live.length === 0 ? (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: "40px 24px",
              color: "var(--text-muted)",
              textAlign: "center",
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius-card)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            <Radio
              size={20}
              strokeWidth={1.25}
              color="var(--text-muted)"
              style={{ display: "block", margin: "0 auto 10px" }}
            />
            No calls in flight
          </div>
        ) : (
          live.map((c) => <LiveCard key={c.call_id} call={c} />)
        )}
      </div>

      {/* Recent section */}
      <SectionHeader
        eyebrow="Recent"
        title={`Completed (${recent.length})`}
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ColumnsPicker columns={CALL_COLUMNS} hidden={hidden} onToggle={onToggleColumn} />
            <StateFilterPicker value={stateFilter} onChange={setStateFilter} />
          </div>
        }
      />

      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-card)",
          overflow: "hidden",
        }}
      >
        {recent.length === 0 ? (
          <div
            style={{
              padding: "44px 24px",
              color: "var(--text-muted)",
              textAlign: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            No recent calls.
          </div>
        ) : (
          <ScrollableTable minWidth={RECENT_TABLE_MIN_WIDTH}>
            <ColumnHeader cols={cols} />
            {recent.map((c, idx) => (
              <RecentRow
                key={c.call_id}
                call={c}
                cols={cols}
                ctx={{ engagementLabel: engagementName.get(c.engagement_id) }}
                index={idx}
              />
            ))}
          </ScrollableTable>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reason cell helpers
// ---------------------------------------------------------------------------

// Reason → short label + accent color for chip styling. Anything not in
// the map renders as the raw reason via the fallback path; that's fine
// for unknown values but keeps the canonical ones consistent with the
// Detail-page chip.
const REASON_LABELS: Record<string, { label: string; tone: "voicemail" | "halt" | "agent" | "system" }> = {
  "agent_initiated:voicemail_detected": { label: "VOICEMAIL", tone: "voicemail" },
  "agent_initiated:wrong_number": { label: "WRONG #", tone: "agent" },
  "agent_initiated:target_unavailable": { label: "UNAVAILABLE", tone: "agent" },
  "agent_initiated:target_refused": { label: "REFUSED", tone: "agent" },
  "agent_initiated:objective_completed": { label: "COMPLETED", tone: "agent" },
  stale_orphan_reaped: { label: "REAPED", tone: "system" },
};

function reasonToneStyle(tone: "voicemail" | "halt" | "agent" | "system"): React.CSSProperties {
  switch (tone) {
    case "voicemail":
      return {
        color: "var(--warning)",
        borderColor: "color-mix(in srgb, var(--warning) 40%, transparent)",
        background: "color-mix(in srgb, var(--warning) 12%, transparent)",
      };
    case "halt":
      return {
        color: "var(--error)",
        borderColor: "color-mix(in srgb, var(--error) 40%, transparent)",
        background: "color-mix(in srgb, var(--error) 12%, transparent)",
      };
    case "system":
      return {
        color: "var(--text-muted)",
        borderColor: "var(--border)",
        background: "var(--bg-tertiary)",
      };
    case "agent":
    default:
      return {
        color: "var(--accent-bright)",
        borderColor: "var(--accent-line)",
        background: "var(--accent-subtle)",
      };
  }
}

function ReasonCell({ reason }: { reason: string | null | undefined }) {
  if (!reason) {
    return (
      <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>—</span>
    );
  }
  const known = REASON_LABELS[reason];
  let label: string;
  let tone: "voicemail" | "halt" | "agent" | "system";
  if (known) {
    label = known.label;
    tone = known.tone;
  } else if (reason.startsWith("halt:")) {
    label = `HALT · ${reason.slice(5).toUpperCase()}`;
    tone = "halt";
  } else if (reason.startsWith("agent_initiated:")) {
    label = reason.slice("agent_initiated:".length).toUpperCase().replace(/_/g, " ");
    tone = "agent";
  } else {
    label = reason.toUpperCase().replace(/_/g, " ");
    tone = "system";
  }
  return (
    <span
      title={reason}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        padding: "2px 7px",
        borderRadius: 3,
        border: "1px solid",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "inline-block",
        maxWidth: "100%",
        ...reasonToneStyle(tone),
      }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StateFilterPicker
// ---------------------------------------------------------------------------

const FILTER_LABELS: Record<StateFilter, string> = {
  ALL: "ALL",
  LIVE: "LIVE",
  ENDED: "ENDED",
  HALTED: "HALTED",
  AGENT_ENDED: "AGENT ENDED",
  VOICEMAIL: "VOICEMAIL",
  WRONG_NUMBER: "WRONG #",
};

function StateFilterPicker({
  value,
  onChange,
}: {
  value: StateFilter;
  onChange: (v: StateFilter) => void;
}) {
  const options: StateFilter[] = [
    "ALL",
    "ENDED",
    "HALTED",
    "AGENT_ENDED",
    "VOICEMAIL",
    "WRONG_NUMBER",
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        background: "var(--bg-secondary)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-input)",
        overflow: "hidden",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
      }}
    >
      {options.map((opt, i) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: "7px 14px",
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--border-subtle)",
              background: active ? "var(--accent-subtle)" : "transparent",
              color: active ? "var(--accent)" : "var(--text-muted)",
              fontFamily: "inherit",
              fontSize: "inherit",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              fontWeight: active ? 600 : 500,
            }}
          >
            {FILTER_LABELS[opt]}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function SectionHeader({
  eyebrow,
  title,
  right,
  accent,
}: {
  eyebrow: string;
  title: string;
  right?: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.18em",
            color: accent ? "var(--accent)" : "var(--text-muted)",
            marginBottom: 4,
          }}
        >
          {eyebrow}
        </div>
        <h3
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: "var(--text-primary)",
          }}
        >
          {title}
        </h3>
      </div>
      {right}
    </div>
  );
}

function StatusBlock({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div
      style={{
        padding: "32px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}

function Dot() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 3,
        height: 3,
        background: "var(--text-muted)",
        margin: "0 10px 2px",
        verticalAlign: "middle",
      }}
    />
  );
}
